<script>
import baseMixin from '@/mixins/baseMixin'
import BookingListTabKey from '@/shared/constants/booking/BookingListTabKey'
import BaseCheckbox from '@/components/_base-checkbox.vue'
import SortOrder from '@/shared/constants/core/SortOrder'
import CustomTooltip from './custom-tooltip.vue'

export default {
  name: 'BookingHeader',
  components: {
    CustomTooltip,
    BaseCheckbox,
  },
  mixins: [baseMixin],
  props: {
    headers: { type: Array, default: () => [] },
    sortKey: { type: String, default: '' },
    sortOrder: { type: String, default: SortOrder.descending },
    allSelected: { type: Boolean, default: false },
    showNotes: { type: Boolean, default: false },
    currentTabName: { type: String, required: true },
    activeTab: { type: [String, Number, Boolean], required: true },
  },
  emits: ['sort', 'toggle-all'],
  data() {
    return {
      BookingListTabKey,
      SortOrder,
    }
  },
  computed: {
    visibleHeaders() {
      return this.headers.filter((h) => this.shouldRenderHeader(h.key))
    },
    isDayByDay() {
      return !!this.activeTab
    },
  },
  methods: {
    handleSort(key) {
      this.$emit('sort', key)
    },
    getSortIcon(key) {
      if (this.sortKey !== key) return 'mdi-swap-vertical'
      return this.sortOrder === SortOrder.ascending
        ? 'mdi-arrow-up-thin'
        : 'mdi-arrow-down-thin'
    },
    isSortable(key) {
      return !['grade', 'certifications', 'notes', 'client'].includes(key)
    },
    shouldRenderHeader(key) {
      const alwaysVisibleMobile = ['date', 'status']

      if (alwaysVisibleMobile.includes(key)) {
        // Special case: hide status on desktop if !isDayByDay
        if (key === 'status' && !this.isMobile && !this.isDayByDay) {
          return false
        }
        return true
      }

      if (key === 'educator') {
        if (this.isMobile) {
          return this.currentTabName !== BookingListTabKey.approvalRequired // visible on mobile except ApprovalRequired
        }
        return ![
          BookingListTabKey.approvalRequired,
          BookingListTabKey.cancelled,
        ].includes(this.currentTabName) // hide on desktop for these tabs
      }

      if (key === 'classification') {
        if (this.isMobile) {
          return this.currentTabName === BookingListTabKey.approvalRequired // only on mobile in ApprovalRequired
        }
        return true
      }

      // Default: show on desktop, hide on mobile
      return !this.isMobile
    },

    hideEducatorOnDesktop(key) {
      if (key !== 'educator') return false
      return [
        BookingListTabKey.cancelled,
        BookingListTabKey.approvalRequired,
      ].includes(this.currentTabName)
    },
  },
}
</script>

<template>
  <thead>
    <tr class="bookind-head header-wrapper">
      <td class="checkbox-cell">
        <BaseCheckbox
          md
          class="custom-checkbox"
          :model-value="allSelected"
          @update:modelValue="$emit('toggle-all')"
        ></BaseCheckbox>
      </td>
      <td
        v-for="header in visibleHeaders"
        :key="`header-${header.key}`"
        :class="[
          'header-cell',
          `${header.key}-header`,
          { sortable: isSortable(header.key) },
          { 'small-cell': ['certifications', 'notes'].includes(header.key) },
        ]"
        :style="{ '--col-width': header.width }"
        class="fixed-col"
        @click="isSortable(header.key) && handleSort(header.key)"
      >
        <div class="header-content">
          {{ header.label }}
          <CustomTooltip
            position="bottom"
            :disable="!isSortable(header.key)"
            :text="$t('customeToolTip.sort')"
          >
            <i
              v-if="isSortable(header.key)"
              class="mdi"
              :class="getSortIcon(header.key)"
            ></i>
          </CustomTooltip>
        </div>
      </td>
      <td
        v-if="
          !isMobile && currentTabName !== BookingListTabKey.approvalRequired
        "
        class="notes-col"
        >{{ this.$t('booking.details.info.notesLabel') }}</td
      >
      <td
        v-if="
          !isMobile && currentTabName === BookingListTabKey.approvalRequired
        "
        class="notes-col"
      ></td>
      <td v-if="isMobile" class="right-dots"></td>
    </tr>
  </thead>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-header.scss';
</style>
